import React from 'react';

import { observer } from 'mobx-react';
import { MultipleSelectionActions, ActionKey } from '@bringg-frontend/monitoring-shared';
import { Task } from '@bringg/types';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { useStores } from 'bringg-web';
import { useHasFeatureFlag } from '@bringg-frontend/hooks';

import UserAssignerTool from './user-assigner-tool';
import useMonitoringStore from '../hooks/use-monitoring-store';
import { useSelectedStore } from '../hooks/use-selected-store';
import { View } from '../types';
import useRouteMonitoringStore from '../../routes-monitoring-screen/stores/helpers/use-route-monitoring-store';
import { updateEtaAfterTasksCancel } from '../../routes-monitoring-screen/utils/cancel-tasks';
import RoutesPlannerOpener from './routes-planner-opener/routes-planner-opener';
import VehicleAssignerTool from '../../routes-monitoring-screen/components/vehicle-assigner-tool';
import ReasonToCancelTask from '../../../orders/reason-to-cancel-task-modal/reason-to-cancel-task-modal';

interface ActionsContainerProps {
	selectedColor: string;
}

const ActionsContainer = ({ selectedColor }: ActionsContainerProps) => {
	const { runStore } = useStores();
	const isAllowMultiSelect = useHasFeatureFlag('allow_routes_monitoring_multi_select');
	const { isModalOpen, toggleModalVisibility, modalSourceType, view, closeModal, getModalParams } =
		useMonitoringStore();
	const {
		store: { selectedTasks, clearSelectedTasks, selectMany }
	} = useSelectedStore();

	const {
		routesMonitoringTableViewStore: { checkedRoutes }
	} = useRouteMonitoringStore();

	const onCancelTasks = async () => {
		if (view === 'routes') {
			await updateEtaAfterTasksCancel(selectedTasks);
		}
		toggleModalVisibility(ActionKey.cancelTask, false, 'tasks');
		clearSelectedTasks();
	};

	const getSelectedTasks = (modalType: ActionKey): Task[] => {
		if (view === View.tasks || modalSourceType(modalType) === 'tasks') {
			return selectedTasks;
		}

		const checkRoutes = runStore.getByIds(checkedRoutes);
		const tasksIds = checkRoutes.map(route => route.task_ids).flat();
		const tasks = getRootEnv().dashboardSdk.sdk.v2.tasks.getMany(tasksIds);

		return tasks;
	};

	return (
		<div>
			{isModalOpen(ActionKey.cancelTask) && (
				<ReasonToCancelTask
					tasks={getSelectedTasks(ActionKey.cancelTask)}
					onCancelTasks={onCancelTasks}
					onModalClose={() => closeModal(ActionKey.cancelTask)}
				/>
			)}

			{isModalOpen(ActionKey.userAssigner) && (
				<UserAssignerTool
					closeModal={() => closeModal(ActionKey.userAssigner)}
					selectedTasks={getSelectedTasks(ActionKey.userAssigner)}
					{...getModalParams(ActionKey.userAssigner)}
				/>
			)}

			{isModalOpen(ActionKey.routesPlanner) && (
				<RoutesPlannerOpener
					closeModal={closeModal}
					selectedTasks={getSelectedTasks(ActionKey.routesPlanner)}
				/>
			)}

			{isModalOpen(ActionKey.vehicleAssigner) && (
				<VehicleAssignerTool
					routeId={getModalParams(ActionKey.vehicleAssigner)?.routeId}
					onCloseModal={() => closeModal(ActionKey.vehicleAssigner)}
					{...getModalParams(ActionKey.vehicleAssigner)}
				/>
			)}

			{isAllowMultiSelect && !!selectedTasks.length && (
				<MultipleSelectionActions
					color={selectedColor}
					tasks={selectedTasks}
					clearSelectedTasks={clearSelectedTasks}
					toggleModal={(key, isOpen) => toggleModalVisibility(key as ActionKey, isOpen, 'tasks')}
					selectMultipleTasks={selectMany}
				/>
			)}
		</div>
	);
};

export default observer(ActionsContainer);
